import React from 'react';
import { cn } from '../lib/utils';

/**
 * Tabs — accessible tab list with controlled state.
 */
export function Tabs({ value, onChange, items, className }) {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex items-center gap-1 p-1 bg-surface-2 border border-border rounded-lg',
        className
      )}
    >
      {items.map((item) => (
        <button
          key={item.value}
          role="tab"
          aria-selected={value === item.value}
          onClick={() => onChange(item.value)}
          className={cn(
            'relative inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
            value === item.value
              ? 'bg-surface text-fg shadow-sm'
              : 'text-fg-muted hover:text-fg'
          )}
        >
          {item.icon && <item.icon className="h-3.5 w-3.5" />}
          {item.label}
          {item.badge != null && (
            <span
              className={cn(
                'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-md text-2xs font-semibold',
                value === item.value
                  ? 'bg-brand/20 text-brand'
                  : 'bg-surface-3 text-fg-muted'
              )}
            >
              {item.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Stat — single metric card with label, value, optional delta.
 */
export function Stat({
  label,
  value,
  hint,
  delta,           // { value, direction: 'up' | 'down' }
  icon: Icon,
  className,
}) {
  return (
    <div className={cn('rounded-lg bg-surface border border-border p-5', className)}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="text-xs font-medium text-fg-muted uppercase tracking-wide">
            {label}
          </div>
          <div className="mt-2 text-2xl font-semibold text-fg tabular-nums">
            {value}
          </div>
          {(hint || delta) && (
            <div className="mt-2 flex items-center gap-1.5 text-2xs">
              {delta && (
                <span
                  className={cn(
                    'inline-flex items-center gap-0.5 font-medium',
                    delta.direction === 'up' ? 'text-success' : 'text-destructive'
                  )}
                >
                  {delta.direction === 'up' ? '↑' : '↓'} {delta.value}
                </span>
              )}
              {hint && <span className="text-fg-subtle">{hint}</span>}
            </div>
          )}
        </div>
        {Icon && (
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-surface-2 border border-border">
            <Icon className="h-4 w-4 text-fg-muted" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * SectionHeader — page-level title bar.
 */
export function SectionHeader({ title, description, actions, className }) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div>
        <h1 className="text-2xl font-semibold text-fg tracking-tight">{title}</h1>
        {description && (
          <p className="text-sm text-fg-muted mt-1 max-w-2xl">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}